import { Modal, InlineConfirmation, InlineAlert } from '@/components';
import { R } from '@/features/profile/resources/ProfileResources.resx';
import { IKid } from '@/shared/interfaces';
import * as React from 'react';
import { useDeleteChild } from '../lib/useDeleteChild';

export const ConfirmDeletionModal = ({
    kid,
    onClose,
    onMutationSuccess,
    title,
}: {
    kid: IKid; // The child to delete
    onClose: () => void;
    onMutationSuccess?: () => Promise<void>;
    title?: string;
}) => {
    const { mutate, status, reset, errorMessage } = useDeleteChild();

    const [runningOnMutationSuccess, setRunningOnMutationSuccess] =
        React.useState(false);

    const onSuccess = async () => {
        setRunningOnMutationSuccess(true);
        try {
            await onMutationSuccess();
        } finally {
            setRunningOnMutationSuccess(false);
        }
        onClose(); // Close the modal when the mutation succeeds
        reset(); // Reset mutation state to avoid unnecessary re-renders
    };

    const confirmationProps = {
        confirmButtonText: R.DeleteProfile,
        message: R.RemoveChildText,
        confirmButtonClick: () => {
            if (status !== 'pending') {
                mutate(kid.UID, { onSuccess });
            }
        },
        cancelButtonText: R.KeepProfile,
        onClose: onClose,
        isPending: status === 'pending' || runningOnMutationSuccess,
    };

    const inlineAlertProps = {
        onClose: () => {
            reset();
            onClose();
        },
        errorMessage,
    };

    return (
        <Modal onClose={onClose} title={title}>
            {status !== 'error' && (
                <InlineConfirmation {...confirmationProps} />
            )}
            {status === 'error' && <InlineAlert {...inlineAlertProps} />}
        </Modal>
    );
};
